import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Upload, Info, RotateCw, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CARD_W = 600;
const CARD_H = 940;

const CAT_LABEL = {
  tackle: "TACKLE",
  flag_masculino: "FLAG MASCULINO",
  flag_femenino: "FLAG FEMENINO",
};

const STATUS = {
  active: { label: "ACTIVO", color: "#34C759" },
  injured: { label: "LESIONADO", color: "#FF9F0A" },
  suspended: { label: "SUSPENDIDO", color: "#FF3B30" },
};

const COVERAGE = {
  fonasa: "Fonasa", isapre: "Isapre", otro: "Otra", sin: "Sin cobertura",
};

function loadImage(src) {
  // Para blob: URLs (same-origin) NO podemos usar crossOrigin="anonymous"
  // porque ataint la imagen y rompe canvas.toBlob() después. Solo lo activamos
  // para HTTP(S) externos.
  return new Promise((res, rej) => {
    const img = new Image();
    if (src && !src.startsWith("blob:") && !src.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

function calcAge(birthDate) {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (isNaN(b.getTime())) return null;
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
  return age;
}

// Para multi-categoría: devuelve [{cat, label, jersey, position}, ...] resolviendo
// los overrides de profile.per_category con fallback al jersey/position top-level
// (que es el de la categoría primaria). Filtra categorías sin info útil.
function effectivePerCategory(profile) {
  const cats = profile?.categories || [];
  const pc = profile?.per_category || {};
  return cats.map(c => {
    const sub = pc[c] || {};
    const jersey = sub.jersey_number != null && sub.jersey_number !== ""
      ? sub.jersey_number
      : profile?.jersey_number;
    const pos1 = sub.position || (cats[0] === c ? profile?.position : null);
    const pos2 = sub.position_secondary || (cats[0] === c ? profile?.position_secondary : null);
    const positions = [pos1, pos2].filter(Boolean).join(", ") || "—";
    return { cat: c, label: CAT_LABEL[c] || c.toUpperCase(), jersey: jersey ?? null, positions };
  });
}

// ============== SHARED FRAME ==============
function drawFrame(ctx, { headerKicker, headerTitle, clubLogo }) {
  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, CARD_H);
  bg.addColorStop(0, "#0a1628");
  bg.addColorStop(0.5, "#0e1a2e");
  bg.addColorStop(1, "#050b16");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Watermark
  if (clubLogo) {
    try {
      ctx.save();
      ctx.globalAlpha = 0.06;
      const size = 460;
      ctx.drawImage(clubLogo, (CARD_W - size) / 2, (CARD_H - size) / 2, size, size);
      ctx.restore();
    } catch {}
  }

  // Double cyan border
  ctx.strokeStyle = "#22d3ee";
  ctx.lineWidth = 4;
  ctx.strokeRect(8, 8, CARD_W - 16, CARD_H - 16);
  ctx.strokeStyle = "rgba(34,211,238,0.4)";
  ctx.lineWidth = 1;
  ctx.strokeRect(20, 20, CARD_W - 40, CARD_H - 40);

  // Header
  ctx.fillStyle = "rgba(34,211,238,0.12)";
  ctx.fillRect(20, 20, CARD_W - 40, 70);
  ctx.fillStyle = "#22d3ee";
  ctx.font = "bold 13px Arial";
  ctx.textAlign = "left";
  ctx.fillText(headerKicker, 40, 50);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 22px Arial";
  ctx.fillText(headerTitle, 40, 78);

  if (clubLogo) {
    try { ctx.drawImage(clubLogo, CARD_W - 100, 28, 60, 60); } catch {}
  }
}

// ============== FRONT ==============
async function renderFront(ctx, { user, profile, photoImg, qrCanvas, clubLogo }) {
  drawFrame(ctx, { headerKicker: "CREDENCIAL DIGITAL · CLUB OFICIAL", headerTitle: "FERROVIARIOS", clubLogo });

  // Photo
  const photoSize = 220;
  const photoX = (CARD_W - photoSize) / 2;
  const photoY = 130;
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(photoX - 6, photoY - 6, photoSize + 12, photoSize + 12);
  ctx.strokeStyle = "#22d3ee";
  ctx.lineWidth = 2;
  ctx.strokeRect(photoX - 6, photoY - 6, photoSize + 12, photoSize + 12);
  if (photoImg) {
    try { ctx.drawImage(photoImg, photoX, photoY, photoSize, photoSize); }
    catch { drawInitial(ctx, photoX, photoY, photoSize, user.name); }
  } else { drawInitial(ctx, photoX, photoY, photoSize, user.name); }

  // Status chip
  const st = STATUS[profile?.status || "active"] || STATUS.active;
  ctx.fillStyle = st.color;
  ctx.fillRect(photoX - 6, photoY - 36, 110, 24);
  ctx.fillStyle = "#0a0a0a";
  ctx.font = "bold 11px Arial";
  ctx.textAlign = "center";
  ctx.fillText(st.label, photoX + 49, photoY - 19);

  // Edad (el número de camiseta ya aparece en cada fila de POSICIÓN abajo,
  // así que se removió el bloque grande "64 CAMISETA" para evitar redundancia
  // y mantener un diseño consistente entre mono y multi-categoría).
  const age = calcAge(profile?.birth_date);
  if (age != null) {
    ctx.fillStyle = "rgba(34,211,238,0.5)";
    ctx.font = "bold 11px Arial";
    ctx.textAlign = "right";
    ctx.fillText("EDAD", CARD_W - 50, photoY + 130);
    ctx.fillStyle = "#fff";
    ctx.font = "bold 56px Arial Black";
    ctx.fillText(`${age}`, CARD_W - 50, photoY + 180);
    ctx.fillStyle = "rgba(34,211,238,0.5)";
    ctx.font = "bold 10px Arial";
    ctx.fillText("AÑOS", CARD_W - 50, photoY + 198);
  }

  // Name
  let y = photoY + photoSize + 40;
  ctx.fillStyle = "#fff";
  ctx.font = "bold 30px Arial";
  ctx.textAlign = "center";
  ctx.fillText((user.name || "").toUpperCase(), CARD_W / 2, y);
  y += 28;

  // Branches only (no role)
  const cats = (profile?.categories || []).map(c => CAT_LABEL[c] || c.toUpperCase());
  if (cats.length) {
    ctx.fillStyle = "#22d3ee";
    ctx.font = "bold 13px Arial";
    ctx.fillText(cats.join(" · "), CARD_W / 2, y);
  } else {
    ctx.fillStyle = "rgba(34,211,238,0.5)";
    ctx.font = "italic 12px Arial";
    ctx.fillText("Sin rama asignada", CARD_W / 2, y);
  }
  y += 36;

  // Divider
  ctx.strokeStyle = "rgba(34,211,238,0.3)";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(60, y); ctx.lineTo(CARD_W - 60, y); ctx.stroke();
  y += 24;

  // Data rows · SIEMPRE per-categoría (mismo diseño con 1, 2 o 3 ramas)
  const perCat = effectivePerCategory(profile);
  ctx.textAlign = "left";

  // RUT siempre primero
  ctx.fillStyle = "#71717A"; ctx.font = "bold 10px Arial"; ctx.fillText("RUT", 60, y);
  ctx.fillStyle = "#fff"; ctx.font = "16px Arial"; ctx.fillText(profile?.rut || "—", 60, y + 18);
  y += 42;

  // Una fila por rama asignada — siempre con label en cyan y jersey en
  // grande/destacado al final de la línea de label (consistente para 1, 2 o N).
  // Si el jugador no tiene categorías asignadas, fallback a una fila genérica.
  const rows = perCat.length
    ? perCat
    : [{ label: "POSICIÓN", jersey: profile?.jersey_number ?? null, positions: profile?.position || "—" }];
  for (const pc of rows) {
    // Label cyan: "POSICIÓN <RAMA>"
    const labelText = perCat.length ? `POSICIÓN ${pc.label}` : pc.label;
    ctx.fillStyle = "#22d3ee";
    ctx.font = "bold 10px Arial";
    ctx.fillText(labelText, 60, y);

    // Jersey destacado a la derecha de la línea de label
    if (pc.jersey != null && pc.jersey !== "") {
      ctx.fillStyle = "#22d3ee";
      ctx.font = "bold 18px Arial Black";
      ctx.textAlign = "right";
      ctx.fillText(`#${pc.jersey}`, CARD_W - 60, y + 2);
      ctx.textAlign = "left";
    }

    // Valor (posiciones) en blanco grande
    ctx.fillStyle = "#fff";
    ctx.font = "16px Arial";
    ctx.fillText(pc.positions || "—", 60, y + 22);
    y += 46;
  }

  // CLUB + ID
  ctx.fillStyle = "#71717A"; ctx.font = "bold 10px Arial"; ctx.fillText("CLUB", 60, y);
  ctx.fillStyle = "#fff"; ctx.font = "16px Arial"; ctx.fillText("FERROVIARIOS · PUENTE ALTO", 60, y + 18);
  y += 42;
  ctx.fillStyle = "#71717A"; ctx.font = "bold 10px Arial"; ctx.fillText("ID", 60, y);
  ctx.fillStyle = "#fff"; ctx.font = "16px Arial"; ctx.fillText((user.user_id || "").slice(-12).toUpperCase(), 60, y + 18);
  y += 42;

  // QR
  if (qrCanvas) {
    const qrSize = 160;
    const qrX = CARD_W - qrSize - 40;
    const qrY = CARD_H - qrSize - 60;
    ctx.fillStyle = "#22d3ee";
    ctx.fillRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12);
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
    ctx.fillStyle = "#22d3ee";
    ctx.font = "bold 11px Arial";
    ctx.textAlign = "right";
    ctx.fillText("ESCANEA PARA VALIDAR", CARD_W - 40, qrY - 16);
  }

  // Footer
  ctx.fillStyle = "rgba(34,211,238,0.5)";
  ctx.font = "10px Arial";
  ctx.textAlign = "left";
  ctx.fillText(`Emitido ${new Date().toLocaleDateString("es-CL")}`, 40, CARD_H - 30);
  ctx.fillText("Esta credencial es personal e intransferible", 40, CARD_H - 16);
}

// ============== BACK ==============
function drawSectionHeader(ctx, label, x, y, w) {
  ctx.fillStyle = "rgba(34,211,238,0.1)";
  ctx.fillRect(x, y, w, 36);
  ctx.strokeStyle = "rgba(34,211,238,0.4)";
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, 36);
  ctx.fillStyle = "#22d3ee";
  ctx.font = "bold 12px Arial";
  ctx.textAlign = "left";
  ctx.fillText(label, x + 14, y + 23);
}

function drawField(ctx, { label, value, x, y, w }) {
  ctx.fillStyle = "#71717A";
  ctx.font = "bold 10px Arial";
  ctx.textAlign = "left";
  ctx.fillText(label, x, y);
  ctx.fillStyle = "#fff";
  ctx.font = "16px Arial";
  // Wrap simple
  const maxChars = Math.floor(w / 9);
  const text = (value || "—").length > maxChars ? value.slice(0, maxChars - 1) + "…" : (value || "—");
  ctx.fillText(text, x, y + 22);
}

async function renderBack(ctx, { user, profile, clubLogo }) {
  drawFrame(ctx, { headerKicker: "REVERSO · DATOS DE EMERGENCIA", headerTitle: "FERROVIARIOS", clubLogo });

  let y = 120;
  const x = 40;
  const w = CARD_W - 80;

  // ---- Emergency Contact ----
  drawSectionHeader(ctx, "📞  CONTACTO DE EMERGENCIA", x, y, w);
  y += 56;
  drawField(ctx, { label: "NOMBRE", value: profile?.emergency_contact || "—", x: x + 8, y, w: w / 2 - 8 });
  drawField(ctx, { label: "TELÉFONO", value: profile?.emergency_phone || "—", x: x + w / 2 + 8, y, w: w / 2 - 8 });
  y += 52;
  drawField(ctx, { label: "TELÉFONO PERSONAL", value: profile?.phone || "—", x: x + 8, y, w: w - 16 });
  y += 60;

  // ---- Medical Info ----
  drawSectionHeader(ctx, "❤  INFORMACIÓN MÉDICA", x, y, w);
  y += 56;
  drawField(ctx, { label: "GRUPO SANGUÍNEO", value: profile?.blood_type || "—", x: x + 8, y, w: w / 2 - 8 });
  drawField(ctx, { label: "COBERTURA", value: COVERAGE[profile?.health_coverage] || profile?.health_coverage || "—", x: x + w / 2 + 8, y, w: w / 2 - 8 });
  y += 52;
  drawField(ctx, { label: "PLAN / ISAPRE", value: profile?.health_plan || "—", x: x + 8, y, w: w - 16 });
  y += 52;
  drawField(ctx, { label: "ALERGIAS", value: profile?.allergies || "Ninguna conocida", x: x + 8, y, w: w - 16 });
  y += 52;
  // Medical notes (multi-line, max 3 lines)
  ctx.fillStyle = "#71717A"; ctx.font = "bold 10px Arial"; ctx.textAlign = "left";
  ctx.fillText("OBSERVACIONES MÉDICAS", x + 8, y);
  ctx.fillStyle = "#fff"; ctx.font = "13px Arial";
  const notes = profile?.medical_notes || "—";
  const lines = wrapText(ctx, notes, w - 16);
  for (let i = 0; i < Math.min(3, lines.length); i++) {
    ctx.fillText(lines[i], x + 8, y + 22 + i * 18);
  }
  y += 22 + 18 * Math.min(3, Math.max(1, lines.length)) + 28;

  // ---- Important Note ----
  ctx.fillStyle = "rgba(255,159,10,0.10)";
  ctx.strokeStyle = "rgba(255,159,10,0.5)";
  ctx.lineWidth = 1;
  const noteH = 90;
  ctx.fillRect(x, y, w, noteH);
  ctx.strokeRect(x, y, w, noteH);
  ctx.fillStyle = "#FF9F0A";
  ctx.font = "bold 12px Arial";
  ctx.fillText("⚠  NOTA IMPORTANTE", x + 14, y + 22);
  ctx.fillStyle = "#fff";
  ctx.font = "13px Arial";
  ctx.fillText("En caso de emergencia, contacte de inmediato a la", x + 14, y + 44);
  ctx.fillText("persona indicada arriba. Adjunte siempre documento", x + 14, y + 62);
  ctx.fillText("de identidad legal.", x + 14, y + 80);

  // Footer
  ctx.fillStyle = "rgba(34,211,238,0.5)";
  ctx.font = "10px Arial";
  ctx.textAlign = "center";
  ctx.fillText("DISCIPLINA · RESPETO · COMPROMISO", CARD_W / 2, CARD_H - 30);
  ctx.fillStyle = "#22d3ee";
  ctx.font = "bold 10px Arial";
  ctx.fillText("FERROVIARIOS PUENTE ALTO · CLUB DEPORTIVO", CARD_W / 2, CARD_H - 16);
}

function wrapText(ctx, text, maxWidth) {
  const words = (text || "").split(" ");
  const lines = []; let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth) { if (line) lines.push(line); line = w; }
    else { line = test; }
  }
  if (line) lines.push(line);
  return lines.length ? lines : ["—"];
}

function drawInitial(ctx, x, y, size, name) {
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(x, y, size, size);
  ctx.fillStyle = "#22d3ee";
  ctx.font = "bold 100px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText((name?.[0] || "?").toUpperCase(), x + size / 2, y + size / 2);
  ctx.textBaseline = "alphabetic";
}

// Render either single side (front | back) returning dataURL
async function renderSide(side, params) {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W * 2;
  canvas.height = CARD_H * 2;
  const ctx = canvas.getContext("2d");
  ctx.scale(2, 2);
  if (side === "front") await renderFront(ctx, params);
  else await renderBack(ctx, params);
  return canvas;
}

// Render combined front+back side by side for download
async function renderCombined(params) {
  const gap = 40;
  const canvas = document.createElement("canvas");
  canvas.width = (CARD_W * 2 + gap) * 2;
  canvas.height = CARD_H * 2;
  const ctx = canvas.getContext("2d");
  ctx.scale(2, 2);
  // black bg
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, CARD_W * 2 + gap, CARD_H);
  // Front
  await renderFront(ctx, params);
  // Translate to draw back
  ctx.translate(CARD_W + gap, 0);
  await renderBack(ctx, params);
  return canvas;
}

// ============== COMPONENT ==============
// CAMBIO (feb 2026): el jugador NO descarga su credencial. La VE como vista previa
// (frente + reverso) para validar que todo esté bien, y luego presiona
// "GENERAR CREDENCIAL" que la sube silenciosamente al Drive del club. La
// directiva imprime físicamente la credencial (paso que pasa como un ingreso
// para el club).
export default function CredentialCard({ user, profile, photoUrl, clubLogo, clubName }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [side, setSide] = useState("front");
  const [flipping, setFlipping] = useState(false);
  const [frontUrl, setFrontUrl] = useState(null);
  const [backUrl, setBackUrl] = useState(null);
  const paramsRef = useRef(null);

  const qrTarget = `${window.location.origin}/credential/${user?.user_id}`;

  // Pre-render ambos lados para mostrar la vista previa antes de generar.
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        let logoImg = null, photoImg = null, qrCanvas = null;
        try { if (clubLogo) logoImg = await loadImage(clubLogo); } catch {}
        try { if (photoUrl) photoImg = await loadImage(photoUrl); } catch {}
        try {
          qrCanvas = document.createElement("canvas");
          await QRCode.toCanvas(qrCanvas, qrTarget, { width: 160, margin: 1, color: { dark: "#0a0a0a", light: "#22d3ee" } });
        } catch {}
        if (cancel) return;
        const params = { user, profile, photoImg, qrCanvas, clubLogo: logoImg };
        paramsRef.current = params;
        const [fc, bc] = await Promise.all([renderSide("front", params), renderSide("back", params)]);
        if (cancel) return;
        try { setFrontUrl(fc.toDataURL("image/png")); } catch (e) { console.error("front toDataURL", e); }
        try { setBackUrl(bc.toDataURL("image/png")); } catch (e) { console.error("back toDataURL", e); }
      } catch (e) {
        console.error("credential prepare", e);
      }
    })();
    return () => { cancel = true; };
  }, [user, profile, photoUrl, clubLogo, qrTarget]);

  const flip = () => {
    if (flipping) return;
    setFlipping(true);
    setTimeout(() => {
      setSide(s => (s === "front" ? "back" : "front"));
      setTimeout(() => setFlipping(false), 200);
    }, 200);
  };

  // Genera el PNG (front+back) y lo sube al Drive del club. No abre, no descarga.
  const generate = async () => {
    if (!paramsRef.current) {
      toast.error("La credencial aún se está preparando. Intenta de nuevo en unos segundos.");
      return;
    }
    setGenerating(true);
    try {
      const combined = await renderCombined(paramsRef.current);
      const blob = await new Promise((res, rej) => {
        try {
          combined.toBlob((b) => b ? res(b) : rej(new Error("canvas.toBlob devolvió null (posible canvas tainted)")), "image/png");
        } catch (e) { rej(e); }
      });
      const fd = new FormData();
      fd.append("file", blob, "credencial.png");
      await api.post("/players/me/credential", fd);
      setGenerated(true);
      toast.success("Credencial generada correctamente.");
    } catch (err) {
      console.error("credential generate failed", err);
      const detail = err?.response?.data?.detail || err?.message || "Error al generar la credencial. Intenta de nuevo.";
      toast.error(detail);
    } finally { setGenerating(false); }
  };

  const currentUrl = side === "front" ? frontUrl : backUrl;

  return (
    <div className="space-y-3" data-testid="credential-section">
      {/* VISTA PREVIA: front+back con flip. El jugador valida y luego genera. */}
      <div className="relative" style={{ perspective: "1200px" }}>
        <div
          className="relative w-full transition-transform duration-300"
          style={{
            transformStyle: "preserve-3d",
            transform: flipping ? "rotateY(90deg)" : "rotateY(0deg)",
          }}
        >
          {currentUrl ? (
            <img
              src={currentUrl}
              alt={`Vista previa credencial ${side}`}
              className="w-full border-2 border-cyan-400/40 shadow-lg shadow-cyan-500/10 select-none"
              draggable={false}
              data-testid="credential-preview"
              data-cred-side={side}
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none", WebkitUserDrag: "none" }}
            />
          ) : (
            <div className="w-full aspect-[600/940] bg-[#0a1628] border-2 border-cyan-400/20 flex items-center justify-center text-cyan-400/60 text-xs uppercase tracking-widest">
              Preparando vista previa...
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-black/70 text-cyan-400 border border-cyan-400/40 flex items-center gap-1" data-testid="cred-side-label">
          <Eye size={9}/> Vista previa · {side === "front" ? "Frente" : "Reverso"}
        </div>
      </div>

      <button
        onClick={flip}
        disabled={!frontUrl || !backUrl}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors border border-cyan-400/60 text-cyan-400 hover:bg-cyan-400/10 disabled:opacity-50"
        data-testid="flip-credential-btn"
      >
        <RotateCw size={12}/> {side === "front" ? "Ver reverso" : "Ver frente"}
      </button>

      <div className="text-[11px] text-[#A1A1AA] leading-relaxed text-center px-2">
        Revisa que tus datos estén correctos. Cuando estés listo, presiona
        <span className="text-cyan-300/90"> Generar credencial</span> para que quede registrada en el Drive del club.
      </div>

      {generated ? (
        <div
          className="w-full p-4 border border-[#34C759]/60 bg-[#34C759]/10 text-[#34C759] text-xs leading-relaxed flex items-start gap-2"
          data-testid="credential-generated-banner"
        >
          <Info size={14} className="mt-px flex-shrink-0"/>
          <div>
            <div className="font-bold uppercase tracking-wider text-[11px] mb-1">Credencial generada</div>
            Tu credencial se está generando.<br/>
            <strong>Consulta con la directiva para obtenerla físicamente.</strong>
          </div>
        </div>
      ) : null}

      <button
        onClick={generate}
        disabled={generating || !frontUrl}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold transition-colors disabled:opacity-50"
        style={{ background: "linear-gradient(90deg, #0891b2 0%, #06b6d4 100%)", color: "#0a0a0a" }}
        data-testid="generate-credential-btn"
      >
        <Upload size={14}/> {generating ? "GENERANDO..." : generated ? "REGENERAR CREDENCIAL" : "GENERAR CREDENCIAL"}
      </button>
    </div>
  );
}
